import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import { RotateCcw, Activity, FlaskConical, CheckCircle2, Eye } from 'lucide-react';
import { useChallengeApproach, useMyChallengeSubmissions } from '../../features/cases/hooks/use-challenges';

const ComparisonPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedAttempt, setSelectedAttempt] = useState(null);

  const { data: approachData, isLoading } = useChallengeApproach(id);
  const { data: submissionsData } = useMyChallengeSubmissions(id);

  const submissions = submissionsData?.data || [];
  const latestResponse = sessionStorage.getItem(`challenge_response_${id}`) || '';

  // Determine which response to show
  const activeSubmission = selectedAttempt !== null
    ? submissions.find(s => s.attemptNumber === selectedAttempt)
    : null;
  const displayedResponse = activeSubmission ? activeSubmission.response : latestResponse;

  if (isLoading) {
    return (
      <AppShell>
        <div className="flex min-h-screen justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-indigo"></div>
        </div>
      </AppShell>
    );
  }

  const approach = approachData?.data;
  const treatmentPlan = approach?.plan || approach?.treatment;
  const finalDiagnosis = approach?.finalDiagnosis || approach?.diagnosis;

  return (
    <AppShell>
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto py-6 px-4">
          <button
            onClick={() => navigate(`/challenges/${id}/approach`)}
            className="text-gray-500 hover:text-gray-900 flex items-center text-sm font-medium mb-3 transition-colors"
          >
            <span className="mr-1">{"<"}</span> Back to Doctor's Approach
          </button>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Side-by-Side Comparison</h1>
          <p className="text-gray-500 text-sm">Compare your clinical thinking with the doctor's approach</p>
        </div>
      </div>

      <div className="bg-gray-50/50 min-h-screen">
        <div className="max-w-6xl mx-auto py-8 px-4 pb-20">

          {/* Past Attempts Selector */}
          {submissions.length > 1 && (
            <div className="mb-6 flex items-center gap-3 flex-wrap">
              <span className="text-xs text-gray-500 font-medium">View attempt:</span>
              {submissions.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSelectedAttempt(selectedAttempt === s.attemptNumber ? null : s.attemptNumber)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    (selectedAttempt === s.attemptNumber || (selectedAttempt === null && s.attemptNumber === submissions[submissions.length - 1].attemptNumber))
                      ? 'bg-[#7367f0] text-white border-[#7367f0]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#7367f0]'
                  }`}
                >
                  Attempt #{s.attemptNumber}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 items-start">
            {/* Your Approach Column */}
            <div className="space-y-4 min-w-0">
              <div className="bg-[#eff6ff] rounded-lg border border-blue-200 p-4">
                <h3 className="font-semibold text-blue-900 flex items-center justify-between">
                  <span>Your Approach</span>
                  {selectedAttempt && <span className="text-xs font-normal text-blue-600 bg-blue-100/70 px-2 py-0.5 rounded-full">Attempt #{selectedAttempt}</span>}
                </h3>
              </div>
              <div className="bg-white rounded-lg border border-blue-200 p-6 min-h-[450px]">
                <p className="text-gray-700 text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] leading-relaxed">
                  {displayedResponse || <span className="text-gray-400 italic">No response recorded for this attempt.</span>}
                </p>
              </div>
            </div>

            {/* Doctor's Approach Column */}
            <div className="space-y-4 min-w-0">
              <div className="bg-[#ecfdf5] rounded-lg border border-green-200 p-4">
                <h3 className="font-semibold text-green-900">Doctor's Approach</h3>
              </div>
              <div className="bg-white rounded-lg border border-green-200 p-6 min-h-[450px] space-y-6">
                
                {/* Physical Examination Findings */}
                {approach?.physicalExam && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <Activity className="text-teal-600" size={15} />
                      <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Physical Examination Findings</h4>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line break-words [overflow-wrap:anywhere] bg-gray-50/70 p-3 rounded-lg border border-gray-100">
                      {approach.physicalExam}
                    </p>
                  </div>
                )}

                {/* Differential Diagnosis */}
                {approach?.differentialDiagnosis && (
                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center gap-1.5 mb-2">
                      <FlaskConical className="text-indigo-600" size={15} />
                      <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Differential Diagnosis</h4>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line break-words [overflow-wrap:anywhere] bg-gray-50/70 p-3 rounded-lg border border-gray-100">
                      {approach.differentialDiagnosis}
                    </p>
                  </div>
                )}

                {/* Diagnostics Plan / Tests */}
                {(approach?.diagnosticsPlan || approach?.tests?.length > 0) && (
                  <div className="border-t border-gray-100 pt-4">
                    <h4 className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Diagnostics Plan</h4>
                    {approach?.tests?.length > 0 ? (
                      <ul className="list-disc pl-5 space-y-1.5 text-gray-700 text-sm bg-gray-50/70 p-3 rounded-lg border border-gray-100 break-words [overflow-wrap:anywhere]">
                        {approach.tests.map((t, idx) => <li key={idx}>{t}</li>)}
                      </ul>
                    ) : (
                      <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line break-words [overflow-wrap:anywhere] bg-gray-50/70 p-3 rounded-lg border border-gray-100">
                        {approach.diagnosticsPlan}
                      </p>
                    )}
                  </div>
                )}

                {/* Diagnostic Results */}
                {approach?.diagnosticResults && (
                  <div className="border-t border-gray-100 pt-4">
                    <h4 className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Diagnostic Results</h4>
                    <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line break-words [overflow-wrap:anywhere] bg-gray-50/70 p-3 rounded-lg border border-gray-100">
                      {approach.diagnosticResults}
                    </p>
                  </div>
                )}

                {/* Final Diagnosis & Prognosis */}
                {finalDiagnosis && (
                  <div className="border-t border-gray-100 pt-4">
                    <div className="bg-indigo-50/60 border border-indigo-200 rounded-lg p-3.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Eye className="text-indigo-600" size={15} />
                        <h4 className="text-xs font-bold text-indigo-900 uppercase tracking-wider">Final Diagnosis</h4>
                      </div>
                      <p className="text-gray-800 text-sm font-semibold leading-relaxed break-words [overflow-wrap:anywhere]">
                        {finalDiagnosis}
                      </p>
                      {approach?.prognosis && (
                        <div className="mt-2 text-xs text-indigo-800 pt-1 border-t border-indigo-100 break-words [overflow-wrap:anywhere]">
                          <span className="font-semibold">Prognosis: </span>
                          <span>{approach.prognosis}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Treatment Plan */}
                {treatmentPlan && (
                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center gap-1.5 mb-2">
                      <CheckCircle2 className="text-emerald-600" size={15} />
                      <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Treatment Plan</h4>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line break-words [overflow-wrap:anywhere] bg-gray-50/70 p-3 rounded-lg border border-gray-100">
                      {treatmentPlan}
                    </p>
                  </div>
                )}

                {/* Patient Outcome */}
                {approach?.outcome && (
                  <div className="border-t border-gray-100 pt-4">
                    <h4 className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Patient Outcome</h4>
                    <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line break-words [overflow-wrap:anywhere] bg-gray-50/70 p-3 rounded-lg border border-gray-100">
                      {approach.outcome}
                    </p>
                  </div>
                )}

                {/* Fallback if no specific clinical sections are present */}
                {!approach?.physicalExam && !approach?.differentialDiagnosis && !approach?.diagnosticsPlan && !approach?.diagnosticResults && !finalDiagnosis && !treatmentPlan && (
                  <p className="text-gray-400 italic text-sm">No clinical details provided by the doctor.</p>
                )}

              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate(`/challenges/${id}/intake`)}
              className="w-full border border-[#7367f0] text-[#7367f0] hover:bg-[#f8f5ff] py-3.5 rounded-lg font-medium transition-colors text-sm flex items-center justify-center gap-2 shadow-sm"
            >
              <RotateCcw size={15} /> Try Again
            </button>
            <button
              onClick={() => navigate('/challenges')}
              className="w-full bg-[#7367f0] hover:bg-[#5e50ee] text-white py-3.5 rounded-lg font-medium transition-colors text-sm shadow-sm"
            >
              Complete Challenge & Return
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
};

export default ComparisonPage;
